import { useEffect, useState } from "react";
import { StatusBadge } from "@/components/status-badge";
import { RelativeTime } from "@/components/relative-time";
import {
  breakdown,
  elapsedSeconds,
  etaSeconds,
  formatDuration,
} from "@/lib/run-metrics";
import type { RunDetail, RunSnapshot } from "@/api/types";

/**
 * Top-of-page hero: status, push-progress, ETA, runtime. Designed to
 * answer "how is this run going?" at a single glance.
 */
export function RunHero({
  run,
  snapshots,
}: {
  run: RunDetail;
  snapshots: RunSnapshot[];
}) {
  // Re-render once a second so elapsed-time and ETA stay live without
  // waiting for the next progress event. Snapshot history dominates the
  // ETA cost so this is a cheap render.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (run.finished_at) return;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [run.finished_at]);

  const br = breakdown(run);
  const isRunning = run.status === "running";
  const target = br.effectiveTarget;
  const pct = target && target > 0 ? Math.min(100, (br.pushed / target) * 100) : null;
  const eta = isRunning ? etaSeconds(run, snapshots, br) : null;
  const elapsed = elapsedSeconds(run);

  return (
    <section className="card p-4">
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
        {/* A paused run keeps `status: "running"` but suppresses producer
            feeding; surface that distinction in the hero badge so the
            user doesn't think the chart has stalled by itself. */}
        <StatusBadge
          status={run.producer_status === "paused" ? "paused" : run.status}
          className="self-center"
        />
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-semibold tabular-nums">
            {br.pushed.toLocaleString()}
          </span>
          {target != null ? (
            <>
              <span className="text-muted text-sm">/</span>
              <span className="text-sm tabular-nums text-muted">
                {target.toLocaleString()}
              </span>
              {pct != null ? (
                <span className="text-sm tabular-nums text-muted">
                  · {pct.toFixed(pct < 1 ? 2 : 1)}%
                </span>
              ) : null}
            </>
          ) : null}
          <span className="text-xs uppercase tracking-wide text-muted ml-2">
            pushed
          </span>
        </div>

        <div className="flex items-baseline gap-2 text-sm">
          <span className="tabular-nums">{br.extractedTotal.toLocaleString()}</span>
          <span className="text-xs uppercase tracking-wide text-muted">
            extracted
          </span>
          {br.duplicate > 0 && br.pushed > 0 ? (
            <span
              className="text-xs text-muted"
              title="Extracted, but the lead already exists in Close — we deliberately did not push it."
            >
              ({br.duplicate.toLocaleString()} duplicate)
            </span>
          ) : null}
        </div>

        {br.unreachable > 0 ? (
          <div
            className="flex items-baseline gap-2 text-sm"
            title="Target server didn't respond — DNS, network, TLS, or wall-clock timeout. Not an application bug."
          >
            <span className="tabular-nums text-muted">
              {br.unreachable.toLocaleString()}
            </span>
            <span className="text-xs uppercase tracking-wide text-muted">
              unreachable
            </span>
          </div>
        ) : null}

        <div
          className="flex items-baseline gap-2 text-sm"
          title="Internal failure during extraction — worth investigating when non-zero."
        >
          <span className="tabular-nums text-danger">
            {br.errored.toLocaleString()}
          </span>
          <span className="text-xs uppercase tracking-wide text-muted">
            errors
          </span>
        </div>

        <div className="ml-auto flex items-baseline gap-4 text-sm">
          {isRunning && eta != null ? (
            <div
              className="flex items-baseline gap-1.5"
              title="Time to drain pending + estimated remaining upstream URLs at the current 5-minute throughput."
            >
              <span className="text-xs uppercase tracking-wide text-muted">
                ETA
              </span>
              <span className="tabular-nums">~{formatDuration(eta)}</span>
            </div>
          ) : null}
          <div className="flex items-baseline gap-1.5">
            <span className="text-xs uppercase tracking-wide text-muted">
              {run.finished_at ? "Ran for" : "Elapsed"}
            </span>
            <span className="tabular-nums">{formatDuration(elapsed)}</span>
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-xs uppercase tracking-wide text-muted">
              Started
            </span>
            <RelativeTime iso={run.started_at} />
          </div>
        </div>
      </div>

      {pct != null ? (
        <div className="mt-3 h-1.5 w-full rounded-full bg-subtle overflow-hidden">
          <div
            className="h-full bg-success transition-[width] duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      ) : null}
    </section>
  );
}
